import React, { useState, useEffect } from 'react'
import { useTranslation } from '@/shared/contexts/TranslationContext'

export interface DateRange {
  start?: string | null
  end?: string | null
  // Optional preset token saved instead of concrete dates
  preset?: string
}

interface DateRangeFilterProps {
  value: DateRange
  onChange: (range: DateRange) => void
  placeholder?: string
}

export function DateRangeFilter({ 
  value, 
  onChange,
  placeholder = 'Selecione o período'
}: DateRangeFilterProps) {
  const { t } = useTranslation()
  const placeholderText = t('list.quickFilters.period.placeholder') || placeholder
  const [presetValue, setPresetValue] = useState<string>('')
  const [showCustomDates, setShowCustomDates] = useState(false)

  // Preset date ranges
  const presets = [
    {
      value: 'today',
      label: t('list.quickFilters.period.presets.today'),
      getRange: () => {
        const today = new Date().toISOString().split('T')[0]
        return { start: today, end: today }
      }
    },
    {
      value: 'yesterday',
      label: t('list.quickFilters.period.presets.yesterday'),
      getRange: () => {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const date = yesterday.toISOString().split('T')[0]
        return { start: date, end: date }
      }
    },
    {
      value: 'last7days',
      label: t('list.quickFilters.period.presets.last7days'),
      getRange: () => {
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - 6)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        }
      }
    },
    {
      value: 'last30days',
      label: t('list.quickFilters.period.presets.last30days'),
      getRange: () => {
        const end = new Date()
        const start = new Date()
        start.setDate(start.getDate() - 29)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        }
      }
    },
    {
      value: 'thismonth',
      label: t('list.quickFilters.period.presets.thismonth'),
      getRange: () => {
        const now = new Date()
        const start = new Date(now.getFullYear(), now.getMonth(), 1)
        const end = new Date(now.getFullYear(), now.getMonth() + 1, 0)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        }
      }
    },
    {
      value: 'lastmonth',
      label: t('list.quickFilters.period.presets.lastmonth'),
      getRange: () => {
        const now = new Date()
        const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
        const end = new Date(now.getFullYear(), now.getMonth(), 0)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        }
      }
    },
    {
      value: 'thisyear',
      label: t('list.quickFilters.period.presets.thisyear'),
      getRange: () => {
        const now = new Date()
        const start = new Date(now.getFullYear(), 0, 1)
        const end = new Date(now.getFullYear(), 11, 31)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        }
      }
    },
    {
      value: 'lastyear',
      label: t('list.quickFilters.period.presets.lastyear'),
      getRange: () => {
        const now = new Date()
        const start = new Date(now.getFullYear() - 1, 0, 1)
        const end = new Date(now.getFullYear() - 1, 11, 31)
        return {
          start: start.toISOString().split('T')[0],
          end: end.toISOString().split('T')[0]
        }
      }
    }
  ]

  // Check if current value matches a preset or is a saved preset token
  useEffect(() => {
    // Don't override when user is editing custom dates
    if (presetValue === 'custom') return

    // If both empty, clear
    if (!value.start && !value.end && !value.preset) {
      setPresetValue('')
      setShowCustomDates(false)
      return
    }

    // If value carries a preset token, use it
    if (value.preset) {
      setPresetValue(value.preset)
      setShowCustomDates(false)
      return
    }

    // Otherwise try to match by concrete range
    const matchingPreset = presets.find(preset => {
      const range = preset.getRange()
      return range.start === value.start && range.end === value.end
    })

    if (matchingPreset) {
      setPresetValue(matchingPreset.value)
      setShowCustomDates(false)
    } else if (value.start || value.end) {
      setPresetValue('custom')
      setShowCustomDates(true)
    }
  }, [value])

  const handlePresetChange = (selectedValue: string) => {
    setPresetValue(selectedValue)
    
    if (selectedValue === '') {
      // Limpar
      onChange({ start: null, end: null })
      setShowCustomDates(false)
    } else if (selectedValue === 'custom') {
      // Mostrar campos de data personalizados
      setShowCustomDates(true)
      // Manter valores existentes ou limpar
      if (!value.start && !value.end) {
        onChange({ start: null, end: null })
      }
    } else {
      // Aplicar preset
      const preset = presets.find(p => p.value === selectedValue)
      if (preset) {
        // Save preset token instead of concrete dates so it resolves when applied later
        onChange({ preset: selectedValue })
        setShowCustomDates(false)
      }
    }
  }

  const handleDateChange = (field: 'start' | 'end', dateValue: string) => {
    // When user types custom date, clear any preset token
    const newVal: DateRange = { start: value.start ?? null, end: value.end ?? null }
    newVal[field] = dateValue || null
    onChange(newVal)
  }

  return (
    <div className="space-y-3">
      {/* Combo de Presets */}
      <select
        value={presetValue}
        onChange={(e) => handlePresetChange(e.target.value)}
        className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400"
      >
        <option value="">{placeholderText}</option>
        {presets.map((preset) => (
          <option key={preset.value} value={preset.value}>
            {preset.label}
          </option>
        ))}
          <option value="custom">{t('list.quickFilters.period.customLabel')}</option>
      </select>

      {/* Campos de Data Personalizados */}
      {showCustomDates && (
        <div className="grid grid-cols-2 gap-3 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200 dark:border-slate-700">
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              {t('list.quickFilters.period.startLabel')}
            </label>
            <input
              type="date"
              value={value.start || ''}
              onChange={(e) => handleDateChange('start', e.target.value)}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
              {t('list.quickFilters.period.endLabel')}
            </label>
            <input
              type="date"
              value={value.end || ''}
              onChange={(e) => handleDateChange('end', e.target.value)}
              min={value.start || undefined}
              className="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>
      )}
    </div>
  )
}
